import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { InstaDev } from '@/lib/graphql'

interface MasterDataState {
  organizations: any[]
  departments: any[]
  projects: any[]
  employees: any[]
  designations: any[] // roles
  projectTypes: any[]
  loading: {
    organizations: boolean
    departments: boolean
    projects: boolean
    employees: boolean
    designations: boolean
    projectTypes: boolean
  }
  lastFetched: {
    organizations: number | null
    departments: number | null
    projects: number | null
    employees: number | null
    designations: number | null
    projectTypes: number | null
  }
  error: string | null
}

const initialState: MasterDataState = {
  organizations: [],
  departments: [],
  projects: [],
  employees: [],
  designations: [],
  projectTypes: [],
  loading: {
    organizations: false,
    departments: false,
    projects: false,
    employees: false,
    designations: false,
    projectTypes: false,
  },
  lastFetched: {
    organizations: null,
    departments: null,
    projects: null,
    employees: null,
    designations: null,
    projectTypes: null,
  },
  error: null,
}

// Cache duration: 5 minutes (in milliseconds)
const CACHE_DURATION = 5 * 60 * 1000

// Helper to check if cache is valid
const isCacheValid = (lastFetched: number | null): boolean => {
  if (!lastFetched) return false
  return Date.now() - lastFetched < CACHE_DURATION
}

// ============== FETCH THUNKS ==============

export const fetchOrganizations = createAsyncThunk(
  'masterData/fetchOrganizations',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    const state = getState() as { masterData: MasterDataState }
    if (!forceRefresh && isCacheValid(state.masterData.lastFetched.organizations)) {
      return state.masterData.organizations
    }
    try {
      const response = await InstaDev.Organization.getOrganizations(1000)
      return Array.isArray(response) ? response : response?.edges || []
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch organizations')
    }
  }
)

export const fetchDepartments = createAsyncThunk(
  'masterData/fetchDepartments',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    const state = getState() as { masterData: MasterDataState }
    if (!forceRefresh && isCacheValid(state.masterData.lastFetched.departments)) {
      return state.masterData.departments
    }
    try {
      const response = await InstaDev.Organization.getDepartments()
      return Array.isArray(response) ? response : response?.edges || []
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch departments')
    }
  }
)

export const fetchDesignations = createAsyncThunk(
  'masterData/fetchDesignations',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    const state = getState() as { masterData: MasterDataState }
    if (!forceRefresh && isCacheValid(state.masterData.lastFetched.designations)) {
      return state.masterData.designations
    }
    try {
      const response = await InstaDev.Organization.getDesignations()
      return Array.isArray(response) ? response : response?.edges || []
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch designations')
    }
  }
)

export const fetchProjects = createAsyncThunk(
  'masterData/fetchProjects',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    const state = getState() as { masterData: MasterDataState }
    if (!forceRefresh && isCacheValid(state.masterData.lastFetched.projects)) {
      return state.masterData.projects
    }
    try {
      const response = await InstaDev.Project.getProjects(1000)
      return Array.isArray(response) ? response : response?.edges || []
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch projects')
    }
  }
)

export const fetchEmployees = createAsyncThunk(
  'masterData/fetchEmployees',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    const state = getState() as { masterData: MasterDataState }
    if (!forceRefresh && isCacheValid(state.masterData.lastFetched.employees)) {
      return state.masterData.employees
    }
    try {
      const response = await InstaDev.Employee.getEmployees(1000)
      return Array.isArray(response) ? response : response?.edges || []
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch employees')
    }
  }
)

export const fetchProjectTypes = createAsyncThunk(
  'masterData/fetchProjectTypes',
  async (forceRefresh: boolean = false, { getState, rejectWithValue }) => {
    const state = getState() as { masterData: MasterDataState }
    if (!forceRefresh && isCacheValid(state.masterData.lastFetched.projectTypes)) {
      return state.masterData.projectTypes
    }
    try {
      const response = await InstaDev.Project.getProjectTypes(100)
      return Array.isArray(response) ? response : response?.edges || []
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch project types')
    }
  }
)

// Fetch all master data in parallel
export const fetchAllMasterData = createAsyncThunk(
  'masterData/fetchAll',
  async (forceRefresh: boolean = false, { dispatch }) => {
    await Promise.all([
      dispatch(fetchOrganizations(forceRefresh)),
      dispatch(fetchDepartments(forceRefresh)),
      dispatch(fetchDesignations(forceRefresh)),
      dispatch(fetchProjects(forceRefresh)),
      dispatch(fetchEmployees(forceRefresh)),
      dispatch(fetchProjectTypes(forceRefresh)),
    ])
  }
)

const masterDataSlice = createSlice({
  name: 'masterData',
  initialState,
  reducers: {
    // ============== CACHE MANAGEMENT ==============
    clearCache: (state) => {
      state.lastFetched = {
        organizations: null,
        departments: null,
        projects: null,
        employees: null,
        designations: null,
        projectTypes: null,
      }
    },
    clearSpecificCache: (state, action: PayloadAction<keyof MasterDataState['lastFetched']>) => {
      state.lastFetched[action.payload] = null
    },

    // ============== BULK UPDATE ==============
    setOrganizations: (state, action: PayloadAction<any[]>) => {
      state.organizations = action.payload
      state.lastFetched.organizations = Date.now()
    },
    setDepartments: (state, action: PayloadAction<any[]>) => {
      state.departments = action.payload
      state.lastFetched.departments = Date.now()
    },
    setDesignations: (state, action: PayloadAction<any[]>) => {
      state.designations = action.payload
      state.lastFetched.designations = Date.now()
    },
    setProjects: (state, action: PayloadAction<any[]>) => {
      state.projects = action.payload
      state.lastFetched.projects = Date.now()
    },
    setEmployees: (state, action: PayloadAction<any[]>) => {
      state.employees = action.payload
      state.lastFetched.employees = Date.now()
    },
    setProjectTypes: (state, action: PayloadAction<any[]>) => {
      state.projectTypes = action.payload
      state.lastFetched.projectTypes = Date.now()
    },

    // ============== ADD SINGLE ITEM ==============
    addOrganization: (state, action: PayloadAction<any>) => {
      state.organizations.push(action.payload)
    },
    addDepartment: (state, action: PayloadAction<any>) => {
      state.departments.push(action.payload)
    },
    addDesignation: (state, action: PayloadAction<any>) => {
      state.designations.push(action.payload)
    },
    addProject: (state, action: PayloadAction<any>) => {
      state.projects.push(action.payload)
    },
    addEmployee: (state, action: PayloadAction<any>) => {
      state.employees.push(action.payload)
    },
    addProjectType: (state, action: PayloadAction<any>) => {
      state.projectTypes.push(action.payload)
    },

    // ============== UPDATE SINGLE ITEM ==============
    updateOrganization: (state, action: PayloadAction<any>) => {
      const index = state.organizations.findIndex(o => o._id === action.payload._id)
      if (index !== -1) {
        state.organizations[index] = { ...state.organizations[index], ...action.payload }
      }
    },
    updateDepartment: (state, action: PayloadAction<any>) => {
      const index = state.departments.findIndex(d => d._id === action.payload._id)
      if (index !== -1) {
        state.departments[index] = { ...state.departments[index], ...action.payload }
      }
    },
    updateDesignation: (state, action: PayloadAction<any>) => {
      const index = state.designations.findIndex(d => d._id === action.payload._id)
      if (index !== -1) {
        state.designations[index] = { ...state.designations[index], ...action.payload }
      }
    },
    updateProject: (state, action: PayloadAction<any>) => {
      const index = state.projects.findIndex(p => p._id === action.payload._id)
      if (index !== -1) {
        state.projects[index] = { ...state.projects[index], ...action.payload }
      }
    },
    updateEmployee: (state, action: PayloadAction<any>) => {
      const index = state.employees.findIndex(e => e._id === action.payload._id)
      if (index !== -1) {
        state.employees[index] = { ...state.employees[index], ...action.payload }
      }
    },
    updateProjectType: (state, action: PayloadAction<any>) => {
      const index = state.projectTypes.findIndex(pt => pt._id === action.payload._id)
      if (index !== -1) {
        state.projectTypes[index] = { ...state.projectTypes[index], ...action.payload }
      }
    },

    // ============== DELETE SINGLE ITEM ==============
    deleteOrganization: (state, action: PayloadAction<string>) => {
      state.organizations = state.organizations.filter(o => o._id !== action.payload)
    },
    deleteDepartment: (state, action: PayloadAction<string>) => {
      state.departments = state.departments.filter(d => d._id !== action.payload)
    },
    deleteDesignation: (state, action: PayloadAction<string>) => {
      state.designations = state.designations.filter(d => d._id !== action.payload)
    },
    deleteProject: (state, action: PayloadAction<string>) => {
      state.projects = state.projects.filter(p => p._id !== action.payload)
    },
    deleteEmployee: (state, action: PayloadAction<string>) => {
      state.employees = state.employees.filter(e => e._id !== action.payload)
    },
    deleteProjectType: (state, action: PayloadAction<string>) => {
      state.projectTypes = state.projectTypes.filter(pt => pt._id !== action.payload)
    },
  },
  extraReducers: (builder) => {
    // Organizations
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.loading.organizations = true
        state.error = null
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.loading.organizations = false
        state.organizations = action.payload
        state.lastFetched.organizations = Date.now()
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.loading.organizations = false
        state.error = action.payload as string
      })

    // Departments
    builder
      .addCase(fetchDepartments.pending, (state) => {
        state.loading.departments = true
        state.error = null
      })
      .addCase(fetchDepartments.fulfilled, (state, action) => {
        state.loading.departments = false
        state.departments = action.payload
        state.lastFetched.departments = Date.now()
      })
      .addCase(fetchDepartments.rejected, (state, action) => {
        state.loading.departments = false
        state.error = action.payload as string
      })

    // Designations (Roles)
    builder
      .addCase(fetchDesignations.pending, (state) => {
        state.loading.designations = true
        state.error = null
      })
      .addCase(fetchDesignations.fulfilled, (state, action) => {
        state.loading.designations = false
        state.designations = action.payload
        state.lastFetched.designations = Date.now()
      })
      .addCase(fetchDesignations.rejected, (state, action) => {
        state.loading.designations = false
        state.error = action.payload as string
      })

    // Projects
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading.projects = true
        state.error = null
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading.projects = false
        state.projects = action.payload
        state.lastFetched.projects = Date.now()
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading.projects = false
        state.error = action.payload as string
      })

    // Employees
    builder
      .addCase(fetchEmployees.pending, (state) => {
        state.loading.employees = true
        state.error = null
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.loading.employees = false
        state.employees = action.payload
        state.lastFetched.employees = Date.now()
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loading.employees = false
        state.error = action.payload as string
      })

    // Project Types
    builder
      .addCase(fetchProjectTypes.pending, (state) => {
        state.loading.projectTypes = true
        state.error = null
      })
      .addCase(fetchProjectTypes.fulfilled, (state, action) => {
        state.loading.projectTypes = false
        state.projectTypes = action.payload
        state.lastFetched.projectTypes = Date.now()
      })
      .addCase(fetchProjectTypes.rejected, (state, action) => {
        state.loading.projectTypes = false
        state.error = action.payload as string
      })
  },
})

export const {
  // Cache
  clearCache,
  clearSpecificCache,
  // Bulk set
  setOrganizations,
  setDepartments,
  setDesignations,
  setProjects,
  setEmployees,
  setProjectTypes,
  // Add
  addOrganization,
  addDepartment,
  addDesignation,
  addProject,
  addEmployee,
  addProjectType,
  // Update
  updateOrganization,
  updateDepartment,
  updateDesignation,
  updateProject,
  updateEmployee,
  updateProjectType,
  // Delete
  deleteOrganization,
  deleteDepartment,
  deleteDesignation,
  deleteProject,
  deleteEmployee,
  deleteProjectType,
} = masterDataSlice.actions

export default masterDataSlice.reducer

// ============== SELECTORS ==============
export const selectOrganizations = (state: { masterData: MasterDataState }) => state.masterData.organizations
export const selectDepartments = (state: { masterData: MasterDataState }) => state.masterData.departments
export const selectDesignations = (state: { masterData: MasterDataState }) => state.masterData.designations
export const selectProjects = (state: { masterData: MasterDataState }) => state.masterData.projects
export const selectEmployees = (state: { masterData: MasterDataState }) => state.masterData.employees
export const selectProjectTypes = (state: { masterData: MasterDataState }) => state.masterData.projectTypes
export const selectMasterDataLoading = (state: { masterData: MasterDataState }) => state.masterData.loading
export const selectMasterDataError = (state: { masterData: MasterDataState }) => state.masterData.error

// Derived selectors
export const selectActiveProjects = (state: { masterData: MasterDataState }) =>
  state.masterData.projects.filter((p: any) =>
    p.status?.toLowerCase() === 'active' || p.status?.toLowerCase() === 'in_progress'
  )

export const selectActiveEmployees = (state: { masterData: MasterDataState }) =>
  state.masterData.employees.filter((e: any) => !e.employee_left && e.is_active !== false)

// Get by ID selectors
export const selectOrganizationById = (id: string) => (state: { masterData: MasterDataState }) =>
  state.masterData.organizations.find((o: any) => o._id === id)

export const selectDepartmentById = (id: string) => (state: { masterData: MasterDataState }) =>
  state.masterData.departments.find((d: any) => d._id === id)

export const selectDesignationById = (id: string) => (state: { masterData: MasterDataState }) =>
  state.masterData.designations.find((d: any) => d._id === id)

export const selectProjectById = (id: string) => (state: { masterData: MasterDataState }) =>
  state.masterData.projects.find((p: any) => p._id === id)

export const selectEmployeeById = (id: string) => (state: { masterData: MasterDataState }) =>
  state.masterData.employees.find((e: any) => e._id === id)

export const selectProjectTypeById = (id: string) => (state: { masterData: MasterDataState }) =>
  state.masterData.projectTypes.find((pt: any) => pt._id === id)
